import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';

export function TenantSelector() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const {
    selectedTenant,
    tenants,
    setSelectedTenant,
    loading,
  } = useTenant();
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [dropdownPos, setDropdownPos] = React.useState({ top: 0, left: 0 });
  const toggleRef = React.useRef<HTMLAnchorElement>(null);
  const dropdownRef = React.useRef<HTMLUListElement>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedDropdown = dropdownRef.current?.contains(event.target as Node);
      const clickedToggle = toggleRef.current?.contains(event.target as Node);
      if (!clickedDropdown && !clickedToggle) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Only show for global admins
  if (!user?.isGlobalAdmin) {
    return (
      <li className="nav-item">
        <span className="nav-link">
          <i className="bi bi-building me-1"></i>
          <span className="d-none d-md-inline">{selectedTenant?.name || t('tenant.unknown')}</span>
        </span>
      </li>
    );
  }

  const toggleDropdown = () => {
    if (!isDropdownOpen && toggleRef.current) {
      const rect = toggleRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleTenantChange = (tenantId: string) => {
    if (tenantId !== selectedTenant?.tenantId) {
      setSelectedTenant(tenantId);
    }
    setIsDropdownOpen(false);
  };

  const dropdownMenu = isDropdownOpen && createPortal(
    <ul
      ref={dropdownRef}
      className="dropdown-menu show tenant-selector-dropdown"
      style={{
        position: 'fixed',
        top: dropdownPos.top,
        left: dropdownPos.left,
        zIndex: 9999,
        maxHeight: '60vh',
        overflowY: 'auto',
      }}
    >
      {tenants.map((tenant) => (
        <li key={tenant.tenantId}>
          <a
            className={`dropdown-item ${selectedTenant?.tenantId === tenant.tenantId ? 'active' : ''}`}
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleTenantChange(tenant.tenantId);
            }}
          >
            <i className="bi bi-building me-2"></i>
            {tenant.name}
            {selectedTenant?.tenantId === tenant.tenantId && (
              <i className="bi bi-check ms-auto"></i>
            )}
          </a>
        </li>
      ))}
      {tenants.length === 0 && (
        <li>
          <span className="dropdown-item text-muted">{t('tenant.no_tenants', 'No tenants available')}</span>
        </li>
      )}
    </ul>,
    document.body
  );

  return (
    <>
      <li className="nav-item dropdown">
        <a
          ref={toggleRef}
          className={`nav-link dropdown-toggle ${isDropdownOpen ? 'show' : ''}`}
          href="#"
          role="button"
          aria-expanded={isDropdownOpen ? 'true' : 'false'}
          aria-label={t('tenant.select', 'Select tenant')}
          onClick={(e) => {
            e.preventDefault();
            toggleDropdown();
          }}
        >
          <i className="bi bi-building me-1"></i>
          <span className="d-none d-md-inline">
            {loading ? t('common.loading', 'Loading...') : selectedTenant?.name}
          </span>
        </a>
      </li>
      {dropdownMenu}
    </>
  );
}
